import React, { useState } from 'react';
import { StickyNote, Plus, Edit2, Trash2, Check, X as XIcon, Save } from 'lucide-react';
import { toast } from 'react-hot-toast';

/**
 * NotesSection V2 - Individual note entries that can be created, edited, deleted, and highlighted
 *
 * Each note is a separate entity that can be:
 * - Added individually
 * - Edited after creation
 * - Deleted
 * - Highlighted/selected
 *
 * Notes are stored as an array of objects with { id, content, created_at, type }
 */
export default function NotesSectionV2({
  collegeNotes = [],  // Array of note objects OR string (legacy)
  essayNotes = [],    // Array of note objects OR string (legacy)
  onSaveCollegeNotes,
  onSaveEssayNotes,
  saving,
  hasEssaySlot
}) {
  // Handle legacy string format - convert to array format
  const normalizeNotes = (notes) => {
    if (!notes) return [];
    if (Array.isArray(notes)) return notes;

    // If it's a string, try to parse it as JSON first
    if (typeof notes === 'string' && notes.trim()) {
      // Check if it looks like stringified JSON array (starts with '[{')
      if (notes.trim().startsWith('[{')) {
        try {
          const parsed = JSON.parse(notes);
          if (Array.isArray(parsed)) {
            return parsed;
          }
        } catch (e) {
          console.warn('Failed to parse notes JSON:', e);
        }
      }

      // Legacy format: plain string - convert to single note object
      return [{
        id: 'legacy-' + Date.now(),
        content: notes,
        created_at: new Date().toISOString(),
        type: 'legacy'
      }];
    }
    return [];
  };

  const normalizedCollegeNotes = normalizeNotes(collegeNotes);
  const normalizedEssayNotes = normalizeNotes(essayNotes);

  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [isAddingCollege, setIsAddingCollege] = useState(false);
  const [isAddingEssay, setIsAddingEssay] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');

  // Add a new note
  const handleAddNote = (type) => {
    if (!newNoteContent.trim()) {
      toast.error('Note cannot be empty');
      return;
    }

    const newNote = {
      id: Date.now().toString(),
      content: newNoteContent,
      created_at: new Date().toISOString(),
      type
    };

    if (type === 'college') {
      onSaveCollegeNotes([...normalizedCollegeNotes, newNote]);
      setIsAddingCollege(false);
    } else {
      onSaveEssayNotes([...normalizedEssayNotes, newNote]);
      setIsAddingEssay(false);
    }

    setNewNoteContent('');
    toast.success('Note added');
  };

  // Start editing a note
  const startEdit = (note) => {
    setEditingNoteId(note.id);
    setEditingContent(note.content);
  };

  // Save edited note
  const saveEdit = (type) => {
    if (!editingContent.trim()) {
      toast.error('Note cannot be empty');
      return;
    }

    if (type === 'college') {
      const updated = normalizedCollegeNotes.map(note =>
        note.id === editingNoteId ? { ...note, content: editingContent } : note
      );
      onSaveCollegeNotes(updated);
    } else {
      const updated = normalizedEssayNotes.map(note =>
        note.id === editingNoteId ? { ...note, content: editingContent } : note
      );
      onSaveEssayNotes(updated);
    }

    setEditingNoteId(null);
    setEditingContent('');
    toast.success('Note updated');
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingNoteId(null);
    setEditingContent('');
  };

  // Delete a note
  const deleteNote = (noteId, type) => {
    if (!confirm('Delete this note?')) return;

    if (type === 'college') {
      const updated = normalizedCollegeNotes.filter(note => note.id !== noteId);
      onSaveCollegeNotes(updated);
    } else {
      const updated = normalizedEssayNotes.filter(note => note.id !== noteId);
      onSaveEssayNotes(updated);
    }

    toast.success('Note deleted');
  };

  // Render a single note
  const renderNote = (note, type) => {
    const isEditing = editingNoteId === note.id;
    const bgColor = type === 'college' ? 'bg-emerald-50/50' : 'bg-indigo-50/50';
    const borderColor = type === 'college' ? 'border-emerald-200' : 'border-indigo-200';

    return (
      <div
        key={note.id}
        className={`${bgColor} border ${borderColor} rounded-lg p-3 group hover:shadow-sm transition`}
      >
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={editingContent}
              onChange={(e) => setEditingContent(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              rows={3}
              autoFocus
            />
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={cancelEdit}
                className="text-xs text-gray-600 hover:text-gray-700 flex items-center gap-1"
              >
                <XIcon className="w-3 h-3" />
                Cancel
              </button>
              <button
                onClick={() => saveEdit(type)}
                className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1"
              >
                <Check className="w-3 h-3" />
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2">
            <div className="flex-1 text-sm text-slate-700 whitespace-pre-wrap break-words">
              {note.content}
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition flex-shrink-0">
              <button
                onClick={() => startEdit(note)}
                className="p-1 hover:bg-white rounded transition"
                title="Edit note"
              >
                <Edit2 className="w-3 h-3 text-gray-600" />
              </button>
              <button
                onClick={() => deleteNote(note.id, type)}
                className="p-1 hover:bg-white rounded transition"
                title="Delete note"
              >
                <Trash2 className="w-3 h-3 text-gray-600" />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StickyNote className="w-4 h-4 text-amber-600" />
          <h3 className="font-semibold text-sm text-slate-800">Your Notes</h3>
        </div>
        {saving && (
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Save className="w-3 h-3 animate-pulse" />
            <span>Saving...</span>
          </div>
        )}
      </div>

      {/* College Notes */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-slate-700 flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
            College Notes
            <span className="text-slate-400 font-normal">({normalizedCollegeNotes.length})</span>
          </label>
          {!isAddingCollege && (
            <button
              onClick={() => setIsAddingCollege(true)}
              className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
            >
              <Plus className="w-3 h-3" />
              Add Note
            </button>
          )}
        </div>

        {/* Add new note form */}
        {isAddingCollege && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2">
            <textarea
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              placeholder="Type your note..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
              rows={3}
              autoFocus
            />
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => {
                  setIsAddingCollege(false);
                  setNewNoteContent('');
                }}
                className="text-xs text-gray-600 hover:text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAddNote('college')}
                className="px-3 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700"
              >
                Add Note
              </button>
            </div>
          </div>
        )}

        {/* Existing notes */}
        <div className="space-y-2">
          {normalizedCollegeNotes.length === 0 && !isAddingCollege && (
            <div className="text-xs text-slate-400 italic p-3 text-center border border-dashed border-slate-200 rounded-lg">
              No college notes yet. Click "Add Note" to create one.
            </div>
          )}
          {normalizedCollegeNotes.map(note => renderNote(note, 'college'))}
        </div>
      </div>

      {/* Essay Notes (only show if essay slot exists) */}
      {hasEssaySlot && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-slate-700 flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-indigo-500"></span>
              Essay Notes
              <span className="text-slate-400 font-normal">({normalizedEssayNotes.length})</span>
            </label>
            {!isAddingEssay && (
              <button
                onClick={() => setIsAddingEssay(true)}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                <Plus className="w-3 h-3" />
                Add Note
              </button>
            )}
          </div>

          {/* Add new note form */}
          {isAddingEssay && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-2">
              <textarea
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                placeholder="Type your note..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                rows={3}
                autoFocus
              />
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={() => {
                    setIsAddingEssay(false);
                    setNewNoteContent('');
                  }}
                  className="text-xs text-gray-600 hover:text-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleAddNote('essay')}
                  className="px-3 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700"
                >
                  Add Note
                </button>
              </div>
            </div>
          )}

          {/* Existing notes */}
          <div className="space-y-2">
            {normalizedEssayNotes.length === 0 && !isAddingEssay && (
              <div className="text-xs text-slate-400 italic p-3 text-center border border-dashed border-slate-200 rounded-lg">
                No essay notes yet. Click "Add Note" to create one.
              </div>
            )}
            {normalizedEssayNotes.map(note => renderNote(note, 'essay'))}
          </div>
        </div>
      )}

      {/* Helpful tip */}
      <div className="text-xs text-slate-500 bg-slate-50 rounded p-2 border border-slate-200">
        <span className="font-medium">💡 Tip:</span> Add individual notes that can be edited, deleted, and highlighted for reference.
      </div>
    </div>
  );
}
